import { useState } from 'react'
import { useSitesRegistry, useSitesStats, useSyncSites } from '../hooks/useSitesRegistry'
import { AddSiteDialog } from '../components/AddSiteDialog'
import { SiteDrawer } from '../components/SiteDrawer'
import type { MaintenanceSite, SiteSource } from '../types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { toast } from 'sonner'
import {
  Globe,
  Rocket,
  Shield,
  Plus,
  Search,
  RefreshCw,
  ExternalLink,
  Loader2,
  FileText,
  Clock,
  ChevronDown,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

// Source configuration for badges and icons
const sourceConfig: Record<SiteSource, { label: string; variant: 'default' | 'secondary' | 'outline'; icon: typeof Globe; color?: string }> = {
  rocketnet: { label: 'Rocket.net', variant: 'default', icon: Rocket, color: 'bg-orange-500' },
  'auth-server': { label: 'JezPress', variant: 'secondary', icon: Shield, color: 'bg-purple-500' },
  manual: { label: 'Manual', variant: 'outline', icon: FileText },
  'mcp-check': { label: 'MCP', variant: 'outline', icon: Clock },
  external: { label: 'External', variant: 'outline', icon: Globe },
}

function SourceBadge({ source }: { source: SiteSource }) {
  const config = sourceConfig[source] || sourceConfig.external
  const Icon = config.icon

  return (
    <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  )
}

function StatusBadge({ status }: { status: string }) {
  const variant = status === 'active' ? 'default' : status === 'inactive' ? 'secondary' : 'outline'
  return <Badge variant={variant}>{status}</Badge>
}

function StatsCards() {
  const { data: stats, isLoading } = useSitesStats()

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardHeader className="p-4 pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Total Sites
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="text-2xl font-bold">{stats?.total || 0}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Rocket className="h-4 w-4" />
            Rocket.net
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="text-2xl font-bold">{stats?.onRocket || 0}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Shield className="h-4 w-4" />
            With JezPress
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="text-2xl font-bold">{stats?.withJezpress || 0}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Plus className="h-4 w-4" />
            External
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="text-2xl font-bold">{stats?.bySource?.['external'] || 0}</div>
        </CardContent>
      </Card>
    </div>
  )
}

interface SitesTableProps {
  sites: MaintenanceSite[]
  isLoading: boolean
  onRowClick: (site: MaintenanceSite) => void
}

function SitesTable({ sites, isLoading, onRowClick }: SitesTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  if (!sites || sites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Globe className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium">No sites found</h3>
        <p className="text-muted-foreground">
          Sites will appear here after syncing from Rocket.net or auth-server
        </p>
      </div>
    )
  }

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Domain</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>
              <Tooltip>
                <TooltipTrigger className="cursor-help">Added</TooltipTrigger>
                <TooltipContent>When the site was added to the registry</TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sites.map((site) => (
            <TableRow
              key={site.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onRowClick(site)}
            >
              <TableCell>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                  <code className="text-sm font-mono truncate">{site.domain}</code>
                </div>
              </TableCell>
              <TableCell className="max-w-[200px]">
                <span className="truncate block" title={site.name || undefined}>
                  {site.name || '-'}
                </span>
              </TableCell>
              <TableCell>
                <SourceBadge source={site.source} />
              </TableCell>
              <TableCell>
                <StatusBadge status={site.status} />
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {formatDistanceToNow(new Date(site.createdAt), { addSuffix: true })}
              </TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                >
                  <a
                    href={`https://${site.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Visit site"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TooltipProvider>
  )
}

export function SitesRegistryPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('all')
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null)
  const [page, setPage] = useState(1)

  const syncSites = useSyncSites()

  // Determine source filter based on active tab
  const getSourceFilter = (): SiteSource[] | undefined => {
    switch (activeTab) {
      case 'rocket':
        return ['rocketnet']
      case 'jezpress':
        return ['auth-server']
      case 'external':
        return ['external', 'manual']
      default:
        return undefined
    }
  }

  const { data: sitesData, isLoading, error } = useSitesRegistry({
    page,
    limit: 50,
    search: searchQuery || undefined,
    source: getSourceFilter(),
  })

  const sites = sitesData?.sites || []
  const pagination = sitesData?.pagination

  const handleSync = async (source: 'rocketnet' | 'auth-server' | 'all') => {
    try {
      const result = await syncSites.mutateAsync(source)
      toast.success(
        `Sync complete: ${result.sitesCreated} created, ${result.sitesUpdated} updated`
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed')
    }
  }

  const handleSiteClick = (site: MaintenanceSite) => {
    setSelectedSiteId(site.id)
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-destructive">Failed to load sites: {String(error)}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Sites</h1>
          <p className="text-muted-foreground">
            Universal WordPress site registry
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={syncSites.isPending}>
                {syncSites.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Sync
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleSync('rocketnet')}>
                <Rocket className="h-4 w-4 mr-2" />
                Sync from Rocket.net
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSync('auth-server')}>
                <Shield className="h-4 w-4 mr-2" />
                Sync from Auth Server
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleSync('all')}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync All
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <AddSiteDialog />
        </div>
      </div>

      {/* Stats */}
      <StatsCards />

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by domain or name..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(1)
            }}
            className="pl-9"
          />
        </div>
        {pagination && (
          <span className="text-sm text-muted-foreground">
            {pagination.total} sites
          </span>
        )}
      </div>

      {/* Tabs and Table */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1) }}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="rocket">
            <Rocket className="h-4 w-4 mr-1" />
            Rocket.net
          </TabsTrigger>
          <TabsTrigger value="jezpress">
            <Shield className="h-4 w-4 mr-1" />
            JezPress
          </TabsTrigger>
          <TabsTrigger value="external">
            <Globe className="h-4 w-4 mr-1" />
            External
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>
                {activeTab === 'all' && 'All Sites'}
                {activeTab === 'rocket' && 'Rocket.net Sites'}
                {activeTab === 'jezpress' && 'JezPress Sites'}
                {activeTab === 'external' && 'External Sites'}
              </CardTitle>
              <CardDescription>
                {activeTab === 'all' && 'All WordPress sites in the registry'}
                {activeTab === 'rocket' && 'Sites hosted on Rocket.net managed hosting'}
                {activeTab === 'jezpress' && 'Sites with the JezPress plugin installed'}
                {activeTab === 'external' && 'Externally hosted sites tracked manually'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SitesTable
                sites={sites}
                isLoading={isLoading}
                onRowClick={handleSiteClick}
              />

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page <= 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Site Detail Drawer */}
      <SiteDrawer
        siteId={selectedSiteId}
        onClose={() => setSelectedSiteId(null)}
      />
    </div>
  )
}
